import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { CART_JSON_SECTION_ID, cartFromSections } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} summaryList
 * @property {HTMLElement} summaryEmpty
 * @property {HTMLElement} summaryTotal
 * @property {HTMLElement} summaryStatus
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement[]} productCards
 *
 * @typedef {object} BundleLineState
 * @property {HTMLElement} card
 * @property {HTMLElement} line
 * @property {HTMLInputElement} input
 * @property {number} variantId
 * @property {string} productTitle
 * @property {string} productImageSrc
 * @property {string} productImageAlt
 * @property {boolean} hasOnlyDefaultVariant
 * @property {string} variantTitle
 * @property {number} quantity
 * @property {number} unitPrice
 * @property {number} linePrice
 * @property {string} productm
 */
class BundleMetaobjectBuilder extends Component {
  requiredRefs = ['summaryList', 'summaryTotal', 'summaryStatus', 'addButton'];

  #abortController = null;
  #formatter = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
    this.addEventListener('change', this.#handleChange, { signal: this.#abortController.signal });

    this.#updateSummary();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  get #productCards() {
    return Array.isArray(this.refs.productCards) ? this.refs.productCards : [];
  }

  get #moneyFormatter() {
    if (this.#formatter) return this.#formatter;

    this.#formatter = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    });

    return this.#formatter;
  }

  #parseInteger(value, fallback = 0) {
    const parsed = Number.parseInt(value || '', 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  #formatMoney(cents) {
    return this.#moneyFormatter.format(cents / 100);
  }

  #getBundleLines(card) {
    return Array.from(card.querySelectorAll('[data-bundle-line]'));
  }

  #getInput(line) {
    const input = line.querySelector('[data-quantity-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #normalizeInput(input, line) {
    const min = Math.max(1, this.#parseInteger(line.dataset.min, 1));
    const step = Math.max(1, this.#parseInteger(line.dataset.step, 1));
    const max = line.dataset.max ? this.#parseInteger(line.dataset.max, Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
    let quantity = this.#parseInteger(input.value, 0);

    if (quantity <= 0) {
      input.value = '0';
      return 0;
    }

    if (quantity < min) {
      quantity = min;
    }

    if ((quantity - min) % step !== 0) {
      quantity = min + Math.floor((quantity - min) / step) * step;
    }

    quantity = Math.min(quantity, max);
    input.value = String(Math.max(quantity, 0));

    return quantity;
  }

  #getLineState(line) {
    const input = this.#getInput(line);
    const card = line.closest('[data-product-card]');

    if (!input || !(card instanceof HTMLElement)) return null;

    const quantity = this.#parseInteger(input.value, 0);

    return {
      card,
      line,
      input,
      variantId: this.#parseInteger(line.dataset.variantId, 0),
      productTitle: card.dataset.productTitle || '',
      productImageSrc: card.dataset.productImageSrc || '',
      productImageAlt: card.dataset.productImageAlt || '',
      hasOnlyDefaultVariant: card.dataset.hasOnlyDefaultVariant === 'true',
      variantTitle: line.dataset.variantTitle || '',
      quantity,
      unitPrice: this.#parseInteger(line.dataset.price, 0),
      linePrice: this.#parseInteger(line.dataset.price, 0) * quantity,
      productm: line.dataset.productm || '',
    };
  }

  #getSelectedItems() {
    return this.#productCards.flatMap((card) =>
      this.#getBundleLines(card)
        .map((line) => this.#getLineState(line))
        .filter((state) => state && state.quantity > 0 && state.variantId > 0)
    );
  }

  #clearStatus() {
    if (!this.refs.summaryStatus.textContent) return;

    this.refs.summaryStatus.textContent = '';
    this.refs.summaryStatus.classList.remove('is-error');
  }

  #createSummaryItem(item) {
    const listItem = document.createElement('li');
    listItem.className = 'bundle-metaobject__summary-item';

    if (item.productImageSrc) {
      const image = document.createElement('img');
      image.className = 'bundle-metaobject__summary-item-image';
      image.src = item.productImageSrc;
      image.alt = item.productImageAlt || item.productTitle;
      image.loading = 'lazy';
      listItem.appendChild(image);
    }

    const content = document.createElement('div');
    content.className = 'bundle-metaobject__summary-item-content';

    const titleLine = document.createElement('div');
    titleLine.className = 'bundle-metaobject__summary-item-line';

    const title = document.createElement('p');
    title.className = 'bundle-metaobject__summary-item-title text-16';
    title.textContent = `${item.productTitle} x${item.quantity}`;

    const price = document.createElement('strong');
    price.className = 'bundle-metaobject__summary-item-price';
    price.textContent = this.#formatMoney(item.linePrice);

    titleLine.append(title, price);
    content.appendChild(titleLine);

    if (!item.hasOnlyDefaultVariant && item.variantTitle) {
      const meta = document.createElement('p');
      meta.className = 'bundle-metaobject__summary-item-meta';

      const sizeLabel = document.createElement('span');
      sizeLabel.textContent = item.variantTitle;

      meta.appendChild(sizeLabel);
      content.appendChild(meta);
    }

    listItem.appendChild(content);

    return listItem;
  }

  #updateSummary() {
    const selectedItems = this.#getSelectedItems();
    const total = selectedItems.reduce((sum, item) => sum + item.linePrice, 0);

    this.refs.summaryList.innerHTML = '';
    selectedItems.forEach((item) => {
      this.refs.summaryList.appendChild(this.#createSummaryItem(item));
    });

    if (this.refs.summaryEmpty) {
      this.refs.summaryEmpty.classList.toggle('hidden', selectedItems.length > 0);
    }

    this.refs.summaryTotal.textContent = this.#formatMoney(total);
    this.refs.addButton.disabled = selectedItems.length === 0 || this.refs.addButton.dataset.loading === 'true';

    this.#updateFreeShipping(total, selectedItems.length > 0);
    this.querySelector('nw-product-estimated-delivery[data-bundle-mode]')?.syncBundleSelection?.();
  }

  /**
   * @param {number} totalCents
   * @param {boolean} hasSelection
   */
  #updateFreeShipping(totalCents, hasSelection) {
    const row = this.querySelector('[data-bundle-free-shipping]');
    const textEl = this.querySelector('[data-bundle-free-shipping-text]');
    if (!(row instanceof HTMLElement) || !(textEl instanceof HTMLElement)) return;

    if (!hasSelection) {
      row.hidden = true;
      textEl.innerHTML = '';
      return;
    }

    row.hidden = false;

    const threshold = this.#parseInteger(row.dataset.freeShippingThresholdCents, 6000);
    const remaining = Math.max(0, threshold - totalCents);

    if (remaining <= 0) {
      textEl.innerHTML = row.dataset.freeShippingQualifiedTemplate || '';
      return;
    }

    const amount = this.#formatMoney(remaining);
    const template = row.dataset.freeShippingRemainingTemplate || '';
    textEl.innerHTML = template.replaceAll('__AMOUNT__', amount);
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-add-bundle]')) {
      event.preventDefault();
      void this.#handleAddToCart();
      return;
    }

    const actionButton = target.closest('[data-quantity-action]');
    if (!(actionButton instanceof HTMLElement)) return;

    const line = actionButton.closest('[data-bundle-line]');
    if (!(line instanceof HTMLElement)) return;

    const input = this.#getInput(line);
    if (!input) return;


    const min = Math.max(1, this.#parseInteger(line.dataset.min, 1));
    const step = Math.max(1, this.#parseInteger(line.dataset.step, 1));
    const max = line.dataset.max ? this.#parseInteger(line.dataset.max, Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
    const current = this.#parseInteger(input.value, 0);
    const isIncrease = actionButton.dataset.quantityAction === 'increase';

    let nextValue = current;

    if (isIncrease) {
      nextValue = current <= 0 ? min : current + step;
      nextValue = Math.min(nextValue, max);
    } else if (current <= min) {
      nextValue = 0;
    } else {
      nextValue = Math.max(min, current - step);
    }

    input.value = String(Math.max(nextValue, 0));
    this.#clearStatus();
    this.#updateSummary();
  };

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-quantity-input]')) return;

    const line = target.closest('[data-bundle-line]');
    if (!(line instanceof HTMLElement)) return;

    this.#normalizeInput(target, line);
    this.#clearStatus();
    this.#updateSummary();
  };

  async #fetchCart() {
    const cartUrl = this.dataset.cartUrl || '/cart.js';
    const response = await fetch(cartUrl, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  async #handleAddToCart() {
    if (this.refs.addButton.disabled) return;

    const selectedItems = this.#getSelectedItems();
    if (!selectedItems.length) return;

    const items = selectedItems.map((item) => ({
      id: item.variantId,
      quantity: item.quantity,
      ...(item.productm ? { properties: { _productm: item.productm } } : {}),
    }));

    const cartItemComponents = document.querySelectorAll('cart-items-component');
    const sections = new Set();
    cartItemComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sections.add(component.dataset.sectionId);
      }
    });
    sections.add(CART_JSON_SECTION_ID);

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    this.refs.addButton.dataset.loading = 'true';
    this.refs.addButton.disabled = true;
    this.refs.addButton.textContent = this.dataset.addingLabel || this.refs.addButton.textContent || '';
    this.refs.summaryStatus.textContent = '';
    this.refs.summaryStatus.classList.remove('is-error');

    try {
      const response = await fetch(Theme.routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: Array.from(sections).join(','),
          sections_url: window.location.pathname,
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || this.dataset.errorLabel || 'Cart error');
      }

      // The updated cart comes with the response; /cart.js is only a fallback.
      let updatedCart = cartFromSections(payload.sections);

      if (!updatedCart) {
        try {
          updatedCart = await this.#fetchCart();
        } catch (error) {
          console.error('Failed to fetch updated cart after bundle add:', error);
        }
      }

      if (updatedCart?.item_count != null) {
        this.dispatchEvent(
          new CartUpdateEvent(updatedCart, this.id || 'bundle-metaobject-builder', {
            itemCount: updatedCart.item_count,
            source: 'bundle-metaobject-builder',
            sections: payload.sections,
          })
        );
      } else {
        this.dispatchEvent(
          new CartAddEvent(payload, this.id || 'bundle-metaobject-builder', {
            itemCount: totalQuantity,
            source: 'product-form-component',
            sections: payload.sections,
          })
        );
      }

      window.DisfrazzesAnalytics?.publish?.('product_added_to_cart', {
        items: selectedItems.map((item) => ({
          variant_id: item.variantId,
          quantity: item.quantity,
          productm: item.productm === '' || !Number.isFinite(Number(item.productm)) ? null : Number(item.productm),
        })),
      });

      this.refs.summaryStatus.textContent = this.dataset.addedLabel || '';

      this.querySelectorAll('[data-quantity-input]').forEach((element) => {
        if (element instanceof HTMLInputElement) {
          element.value = '0';
        }
      });

      this.#updateSummary();
      window.location.href = Theme.routes.cart_url;
    } catch (error) {
      this.refs.summaryStatus.textContent =
        error instanceof Error ? error.message : this.dataset.errorLabel || 'Cart error';
      this.refs.summaryStatus.classList.add('is-error');
    } finally {
      this.refs.addButton.dataset.loading = 'false';
      this.refs.addButton.textContent = this.dataset.addLabel || '';
      this.#updateSummary();
    }
  }
}

if (!customElements.get('bundle-metaobject-builder')) {
  customElements.define('bundle-metaobject-builder', BundleMetaobjectBuilder);
}
